import { useEffect, useState } from 'react';

/**
 * Reactive matchMedia hook. Returns false until the client can evaluate the query
 * (SSR-safe). Supports both modern and legacy MediaQueryList listeners.
 */
const useMediaQuery = (query: string): boolean => {
    const [matches, setMatches] = useState(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
            return false;
        }
        return window.matchMedia(query).matches;
    });

    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
            return undefined;
        }

        const mql = window.matchMedia(query);
        const onChange = (event?: MediaQueryListEvent) => {
            setMatches(event ? event.matches : mql.matches);
        };

        onChange();

        if (typeof mql.addEventListener === 'function') {
            mql.addEventListener('change', onChange);
            return () => mql.removeEventListener('change', onChange);
        }

        // Older browsers / some embedded WebViews
        mql.addListener(onChange);
        return () => mql.removeListener(onChange);
    }, [query]);

    return matches;
};

export default useMediaQuery;
